import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, Form, Tab, Tabs, Table, Alert, ButtonGroup, Spinner, InputGroup, FormControl } from "react-bootstrap";
import { fetchRegiones, fetchComunasByRegion } from "../../redux/regionComunaSlice";
import RegisterUser from "../../components/Auth/registerUser";
import Swal from 'sweetalert2';
import axios from "../../utils/axiosConfig";

const UsuariosPage = () => {
  const dispatch = useDispatch();
  const { regiones, comunas } = useSelector((state) => state.regionComuna);
  const [usuarios, setUsuarios] = useState([]);
  const [filteredUsuarios, setFilteredUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("listar");
  const [searchTerm, setSearchTerm] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Estados para modales
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);

  // Estado para el usuario actual
  const [currentUser, setCurrentUser] = useState({
    id: "",
    nombre: "",
    primer_apellido: "",
    segundo_apellido: "",
    telefono: "",
    direccion: "",
    email_personal: "",
    contacto_emergencia: "",
    telefono_emergencia: "",
    region_id: "",
    comuna_id: "",
    rol: ""
  });

  // Validaciones
  const [validationErrors, setValidationErrors] = useState({
    nombre: "",
    primer_apellido: "",
    telefono: "",
    email_personal: "",
    contacto_emergencia: "",
    telefono_emergencia: "",
    region_id: "",
    comuna_id: "",
    direccion: ""
  });

  // Cargar regiones y usuarios al montar el componente
  useEffect(() => {
    dispatch(fetchRegiones());
    fetchUsuarios();
  }, [dispatch]);

  // Cargar comunas cuando se seleccione una región
  useEffect(() => {
    if (currentUser?.region_id) {
      dispatch(fetchComunasByRegion(currentUser.region_id));
    }
  }, [currentUser?.region_id, dispatch]);

  // Filtrar usuarios cuando cambia el término de búsqueda
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredUsuarios(usuarios);
    } else {
      const filtered = usuarios.filter(usuario => {
        const searchLower = searchTerm.toLowerCase();
        return (
          usuario.nombre.toLowerCase().includes(searchLower) ||
          usuario.primer_apellido.toLowerCase().includes(searchLower) ||
          usuario.segundo_apellido?.toLowerCase().includes(searchLower) ||
          usuario.email.toLowerCase().includes(searchLower) ||
          usuario.rol.toLowerCase().includes(searchLower)
        );
      });
      setFilteredUsuarios(filtered);
    }
  }, [searchTerm, usuarios]);

  // Obtener la lista de usuarios
  const fetchUsuarios = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get("/auth/admin/listar-usuarios", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setUsuarios(response.data);
      setFilteredUsuarios(response.data);
    } catch (error) {
      console.error("Error al obtener usuarios:", error);
      setError(error.response?.data?.message || "Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  // Validar campos en tiempo real
  const validateField = (name, value) => {
    let error = "";
    const trimmedValue = value ? value.toString().trim() : "";

    // Campos opcionales
    const optionalFields = ["segundo_apellido"];
    if (optionalFields.includes(name) && trimmedValue === "") {
      return "";
    }

    const isEmpty = (val) =>
      typeof val !== "string" || val.trim() === "";

    switch (name) {
      case "nombre":
      case "primer_apellido":
      case "segundo_apellido":
        if (!trimmedValue && name !== "segundo_apellido") {
          error = "Este campo es obligatorio";
        } else if (trimmedValue && !/^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]+$/.test(trimmedValue)) {
          error = "Solo puede contener letras y espacios";
        } else if (trimmedValue && trimmedValue.length < 2) {
          error = "Debe tener al menos 2 caracteres";
        }
        break;
      case "telefono":
        if (isEmpty(value)) {
          error = "Este campo es obligatorio";
        } else if (!/^\+56\d{8,9}$/.test(value.trim())) {
          error = "Formato inválido (+56912345678)";
        }
        break;
      case "email_personal":
        if (isEmpty(value)) {
          error = "Este campo es obligatorio";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          error = "Email inválido";
        }
        break;
      case "contacto_emergencia":
        if (isEmpty(value)) {
          error = "Este campo es obligatorio";
        }
        break;
      case "telefono_emergencia":
        if (isEmpty(value)) {
          error = "Este campo es obligatorio";
        } else if (!/^\+56\d{8,9}$/.test(value.trim())) {
          error = "Formato inválido (+56912345678)";
        }
        break;
      case "region_id":
        if (!value) {
          error = "Debe seleccionar una región";
        }
        break;
      case "comuna_id":
        if (!value) {
          error = "Debe seleccionar una comuna";
        }
        break;
      case "direccion":
        if (isEmpty(value)) {
          error = "Este campo es obligatorio";
        }
        break;
      default:
        break;
    }

    return error;
  };


  // Manejar cambios en los campos del formulario con validación
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validar el campo
    const error = validateField(name, value);
    setValidationErrors({
      ...validationErrors,
      [name]: error
    });

    setCurrentUser(prev => ({ ...prev, [name]: value }));
  };

  // Eliminar Usuario con confirmación
  const eliminarUsuario = async (id) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: "Esta acción no se puede deshacer",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`/auth/admin/eliminar-usuario/${id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });

        await Swal.fire({
          icon: 'success',
          title: 'Usuario eliminado',
          text: 'El usuario se ha eliminado correctamente.',
        });

        fetchUsuarios();
      } catch (error) {
        console.error("Error al eliminar usuario:", error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Hubo un error al eliminar el usuario.',
        });
      }
    }
  };

  // Abrir Modal para Editar Rol
  const openEditRoleModal = (user) => {
    setCurrentUser(user);
    setShowEditRoleModal(true);
  };

  // Actualizar Rol del Usuario
  const actualizarRol = async () => {
    try {

      const datosActualizados = {
        ...currentUser,
        primer_apellido: currentUser.primer_apellido.replace(/\s+/g, ''),
        segundo_apellido: currentUser.segundo_apellido.replace(/\s+/g, ''),
        region_id: parseInt(currentUser.region_id, 10),
        comuna_id: parseInt(currentUser.comuna_id, 10),
      };

      await axios.put(
        `/auth/admin/actualizar-rol-usuario/${currentUser.id}`,
        { rol: currentUser.rol }, datosActualizados,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      await Swal.fire({
        icon: 'success',
        title: 'Rol actualizado',
        text: 'El rol del usuario se ha actualizado correctamente.',
      });

      setShowEditRoleModal(false);
      fetchUsuarios();
    } catch (error) {
      console.error("Error al actualizar rol:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Hubo un error al actualizar el rol.',
      });
    }
  };

  // Abrir Modal para Modificar Usuario
  const openEditUserModal = async (user) => {
    try {
      const response = await axios.get(`/auth/usuario/${user.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      const userData = response.data;

      // Obtener datos de comuna y región si existen
      const comunaData = userData.comuna || userData.usuario_comuna;
      const regionId = comunaData?.region_id || comunaData?.comuna_region?.id || "";
      const comunaId = comunaData?.id || "";

      setCurrentUser({
        ...userData,
        region_id: regionId,
        comuna_id: comunaId,
        // Asegurarse de que los campos sensibles estén desencriptados
        telefono: userData.telefono || "",
        direccion: userData.direccion || "",
        email_personal: userData.email_personal || "",
        contacto_emergencia: userData.contacto_emergencia || "",
        telefono_emergencia: userData.telefono_emergencia || ""
      });

      // Si hay región, cargar sus comunas
      if (regionId) {
        dispatch(fetchComunasByRegion(regionId));
      }

      // Resetear errores de validación
      setValidationErrors({
        nombre: "",
        primer_apellido: "",
        telefono: "",
        email_personal: "",
        contacto_emergencia: "",
        telefono_emergencia: "",
        region_id: "",
        comuna_id: "",
        direccion: ""
      });

      setShowEditUserModal(true);
    } catch (error) {
      console.error("Error al obtener datos del usuario:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Hubo un error al cargar los datos del usuario.',
      });
    }
  };

  // Validar todo el formulario antes de guardar
  const validateForm = () => {
    const errors = {};
    let isValid = true;

    const fieldsToValidate = [
      'nombre', 'primer_apellido', 'telefono',
      'email_personal', 'contacto_emergencia',
      'telefono_emergencia', 'region_id',
      'comuna_id', 'direccion'
    ];

    fieldsToValidate.forEach(field => {
      const error = validateField(field, currentUser[field]);
      errors[field] = error;
      if (error) isValid = false;
    });

    setValidationErrors(errors);
    return isValid;
  };

  // Actualizar Usuario
  const actualizarUsuario = async () => {
    if (!validateForm()) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Por favor corrige los errores en el formulario.',
      });
      return;
    }

    setIsSaving(true);
    try {





      await axios.put(
        `/auth/actualizar-usuario/${currentUser.id}`,
        {
          nombre: currentUser.nombre,
          primer_apellido: currentUser.primer_apellido,
          segundo_apellido: currentUser.segundo_apellido,
          telefono: currentUser.telefono,
          direccion: currentUser.direccion,
          email_personal: currentUser.email_personal,
          contacto_emergencia: currentUser.contacto_emergencia,
          telefono_emergencia: currentUser.telefono_emergencia,
          region_id: parseInt(currentUser.region_id, 10),
          comuna_id: parseInt(currentUser.comuna_id, 10),
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      await Swal.fire({
        icon: 'success',
        title: 'Usuario actualizado',
        text: 'Los datos del usuario se han actualizado correctamente.',
      });

      setShowEditUserModal(false);
      fetchUsuarios();
    } catch (error) {
      console.error("Error al actualizar usuario:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Hubo un error al actualizar el usuario.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="h3 mb-0">Gestión de Usuarios</h1>
        <Button
          onClick={() => setActiveTab("registrar")}
          variant="success"
          className="d-flex align-items-center"
        >
          <i className="bi bi-plus-circle me-2"></i>
          <span className="d-none d-sm-inline">Registrar Usuario</span>
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="mt-3">
          {error}
        </Alert>
      )}

      <Tabs
        id="usuarios-tabs"
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-3"
      >
        <Tab eventKey="registrar" title="Registrar Usuario">
          <RegisterUser onSuccess={() => {
            setActiveTab("listar");
            fetchUsuarios();
          }} />
        </Tab>
        <Tab eventKey="listar" title="Lista de Usuarios">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Cargando usuarios...</p>
            </div>
          ) : (
            <>
              <div className="d-flex justify-content-between mb-3">
                <div className="d-block d-md-none alert alert-info mb-3">
                  <i className="bi bi-phone me-1"></i>
                  Desliza horizontalmente para ver todas las columnas
                </div>

                <div className="w-50">
                  <InputGroup>
                    <FormControl
                      placeholder="Buscar usuarios..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Button variant="outline-secondary">
                      <i className="bi bi-search"></i>
                    </Button>
                  </InputGroup>
                </div>
              </div>

              <div className="table-responsive">
                <Table striped bordered hover className="small">
                  <thead className="table-dark">
                    <tr>
                      <th>#</th>
                      <th>Nombre</th>
                      <th className="d-none d-md-table-cell">Email</th>
                      <th>Rol</th>
                      <th className="text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsuarios.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-4 text-muted">
                          {searchTerm ? "No se encontraron resultados" : "No hay usuarios registrados"}
                        </td>
                      </tr>
                    ) : (
                      filteredUsuarios.map((usuario) => (
                        <tr key={usuario.id}>
                          <td>{usuario.id}</td>
                          <td>
                            {`${usuario.nombre} ${usuario.primer_apellido} ${usuario.segundo_apellido || ''}`.trim()}
                          </td>
                          <td className="d-none d-md-table-cell">
                            {usuario.email}
                          </td>
                          <td>
                            <span className={`badge ${usuario.rol === 'administrador' ? 'bg-primary' : 'bg-success'}`}>
                              {usuario.rol}
                            </span>
                          </td>
                          <td className="text-center">
                            <ButtonGroup size="sm">
                              <Button
                                variant="outline-primary"
                                onClick={() => openEditUserModal(usuario)}
                                title="Editar"
                              >
                                <i className="bi bi-pencil"></i>
                                <span className="d-none d-lg-inline ms-1">Editar</span>
                              </Button>
                              <Button
                                variant="outline-warning"
                                onClick={() => openEditRoleModal(usuario)}
                                title="Cambiar Rol"
                              >
                                <i className="bi bi-arrow-repeat"></i>
                                <span className="d-none d-lg-inline ms-1">Rol</span>
                              </Button>
                              <Button
                                variant="outline-danger"
                                onClick={() => eliminarUsuario(usuario.id)}
                                title="Eliminar"
                              >
                                <i className="bi bi-trash"></i>
                                <span className="d-none d-lg-inline ms-1">Eliminar</span>
                              </Button>
                            </ButtonGroup>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </Tab>
      </Tabs>

      {/* Modal para Editar Rol */}
      <Modal show={showEditRoleModal} onHide={() => setShowEditRoleModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-arrow-repeat me-2"></i>
            Editar Rol de Usuario
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="formRole">
              <Form.Label>Rol</Form.Label>
              <Form.Control
                as="select"
                name="rol"
                value={currentUser.rol || ""}
                onChange={handleChange}
              >
                <option value="socio">Socio</option>
                <option value="administrador">Administrador</option>
              </Form.Control>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowEditRoleModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={actualizarRol}>
            <i className="bi bi-check-circle me-1"></i>
            Guardar Cambios
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal para Modificar Usuario */}
      <Modal show={showEditUserModal} onHide={() => setShowEditUserModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-person-lines-fill me-2"></i>
            Modificar Usuario
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="row">
              <Form.Group className="mb-3 col-md-4" controlId="formNombre">
                <Form.Label>Nombre *</Form.Label>
                <Form.Control
                  type="text"
                  name="nombre"
                  value={currentUser.nombre || ""}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.nombre}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.nombre}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3 col-md-4" controlId="formPrimerApellido">
                <Form.Label>Primer Apellido *</Form.Label>
                <Form.Control
                  type="text"
                  name="primer_apellido"
                  value={currentUser.primer_apellido || ""}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.primer_apellido}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.primer_apellido}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3 col-md-4" controlId="formSegundoApellido">
                <Form.Label>Segundo Apellido</Form.Label>
                <Form.Control
                  type="text"
                  name="segundo_apellido"
                  value={currentUser.segundo_apellido || ""}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.segundo_apellido}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.segundo_apellido}
                </Form.Control.Feedback>
              </Form.Group>
            </div>

            <Form.Group className="mb-3" controlId="formTelefono">
              <Form.Label>Teléfono *</Form.Label>
              <Form.Control
                type="tel"
                name="telefono"
                value={currentUser.telefono || ""}
                onChange={handleChange}
                isInvalid={!!validationErrors.telefono}
                required
                placeholder="+56912345678"
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.telefono}
              </Form.Control.Feedback>
            </Form.Group>

            <div className="row">
              <Form.Group className="mb-3 col-md-6" controlId="formRegion">
                <Form.Label>Región *</Form.Label>
                <Form.Select
                  name="region_id"
                  value={currentUser.region_id || ""}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.region_id}
                  required
                >
                  <option value="">Seleccione una región</option>
                  {regiones.map((region) => (
                    <option key={region.id} value={region.id}>
                      {region.nombre}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {validationErrors.region_id}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3 col-md-6" controlId="formComuna">
                <Form.Label>Comuna *</Form.Label>
                <Form.Select
                  name="comuna_id"
                  value={currentUser.comuna_id || ""}
                  onChange={handleChange}
                  disabled={!currentUser.region_id || comunas.length === 0}
                  isInvalid={!!validationErrors.comuna_id}
                  required
                >
                  <option value="">Seleccione una comuna</option>
                  {comunas
                    .filter((comuna) => comuna.region_id === parseInt(currentUser.region_id, 10))
                    .map((comuna) => (
                      <option key={comuna.id} value={comuna.id}>
                        {comuna.nombre}
                      </option>
                    ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {validationErrors.comuna_id}
                </Form.Control.Feedback>
              </Form.Group>
            </div>

            <Form.Group className="mb-3" controlId="formDireccion">
              <Form.Label>Dirección *</Form.Label>
              <Form.Control
                type="text"
                name="direccion"
                value={currentUser.direccion || ""}
                onChange={handleChange}
                isInvalid={!!validationErrors.direccion}
                required
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.direccion}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formEmailPersonal">
              <Form.Label>Email Personal *</Form.Label>
              <Form.Control
                type="email"
                name="email_personal"
                value={currentUser.email_personal || ""}
                onChange={handleChange}
                isInvalid={!!validationErrors.email_personal}
                required
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.email_personal}
              </Form.Control.Feedback>
            </Form.Group>

            <div className="row">
              <Form.Group className="mb-3 col-md-6" controlId="formContactoEmergencia">
                <Form.Label>Contacto de Emergencia *</Form.Label>
                <Form.Control
                  type="text"
                  name="contacto_emergencia"
                  value={currentUser.contacto_emergencia || ""}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.contacto_emergencia}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.contacto_emergencia}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3 col-md-6" controlId="formTelefonoEmergencia">
                <Form.Label>Teléfono de Emergencia *</Form.Label>
                <Form.Control
                  type="tel"
                  name="telefono_emergencia"
                  value={currentUser.telefono_emergencia || ""}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.telefono_emergencia}
                  required
                  placeholder="+56912345678"
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.telefono_emergencia}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowEditUserModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={actualizarUsuario} disabled={isSaving}>
            {isSaving ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                <span className="ms-2">Guardando...</span>
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-1"></i>
                Guardar Cambios
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UsuariosPage;